import React, { useEffect, useState } from 'react'
import { apiClient, authedAxios } from '../utils'
import { useStore } from '../store'
import { useLocation, useNavigate } from 'react-router-dom'

import CustomerSearch from '../common/CustomerSearch'
import DashboardTabs from './dashboard-tabs'
import {
  Avatar,
  Box,
  IconButton,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Badge,
} from '@mui/material'
import {
  Logout,
  ManageAccounts,
  Menu as MenuIcon,
  Paid,
} from '@mui/icons-material'
import useAvatar from '../hooks/api/useAvatar'
import Notifications from './Notifications'
import { LIONS_CLUB_CAP } from '../constants'
import SetupWatcher from '../SetupWatcher'
import useGenericStoreEndpoint from '../hooks/api/useGenericStoreEndpoint'
import { COMPANY_ACTIONS } from '../store/company'
import useIsMobile, { useScreenSize } from '../hooks/useIsMobile'
import useIsAdmin from '../hooks/useIsAdmin'
import QuickNote from './QuickNote'

const Header = ({ navOpen }) => {
  const [logoUrl, setLogoUrl] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false)
  const [avatarMenuAnchorElement, setAvatarMenuAnchorElement] = useState(null)

  const [logoLoading, setLogoLoading] = useState(true)

  const [dummyAutocompleValue, setDummyAutocompleteValue] = useState(null)

  const { avatarUrl } = useAvatar()

  const { state } = useStore()

  const { identity } = state

  const navigate = useNavigate()
  const { pathname, search } = useLocation()

  const isInSetupFlow = pathname.includes('setup')
  const isInDashboard = pathname.includes('dashboard')

  const initials = `${identity.firstName[0]}${identity.lastName[0]}`
  const name = `${identity.firstName} ${identity.lastName}`

  const onSelectOption = (option) => {
    const isCreateButton = option.id === '__NEW__'
    const isErrorLabel = option.id === '__ERROR__'

    if (isErrorLabel) return
    const url = isCreateButton ? '/customers/new' : `/customers/${option.id}`

    setDummyAutocompleteValue(null)
    setSearchQuery('')
    if (window.location.pathname === url) {
      return
    }

    navigate(url)
  }

  const { company, isLoading, hasLoadError } = useGenericStoreEndpoint(
    '/api/company',
    COMPANY_ACTIONS.SET_COMPANY,
    'company'
  )

  const getCompanyLogo = () => {
    setLogoLoading(true)

    return authedAxios
      .get(`/api/image/company-${company.id}-company-logo`)
      .then(({ data }) => {
        setLogoUrl(data)
        setLogoLoading(false)
      })
      .catch(() => {
        setLogoLoading(false)
      })
  }

  useEffect(() => {
    if (logoUrl || !company || logoLoading) return
    if (!pathname.includes('settings') && !pathname.includes('setup')) return

    getCompanyLogo()
  }, [pathname, logoUrl])

  useEffect(() => {
    if (!company || logoUrl) return

    getCompanyLogo()
  }, [JSON.stringify(company)])

  const leftNavHidden =
    pathname.includes('setup') ||
    (pathname.includes('/billing') && search.includes('unpaid=true'))

  const isMobile = useIsMobile()

  const { windowWidth } = useScreenSize()

  const collapseSearch = windowWidth < 1100

  const lionsClubTracker = company?.lionsClubTracker
  const isInLionsClub = lionsClubTracker && lionsClubTracker <= LIONS_CLUB_CAP

  const { isAdmin } = useIsAdmin()

  const avatar = (
    <Avatar alt={name} src={avatarUrl} sx={{ width: '65px', height: '65px' }}>
      {initials}
    </Avatar>
  )

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          transition: 'all 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
          width: `calc(100% - ${
            isMobile || leftNavHidden ? '0' : navOpen ? '254' : '107'
          }px)`,
          left: isMobile || leftNavHidden ? 0 : navOpen ? '246px' : '93px',
          pl: isMobile || leftNavHidden ? 0 : '14px',
          // height: '120px',
          // boxShadow: ' 0 7px 9px -7px rgb(0 0 0 / 40%)',
          backgroundColor: 'white',
          zIndex: 1000,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <div
          className='header-container'
          style={{ paddingBottom: isInDashboard ? 0 : null }}
        >
          {company && !logoLoading && (
            <>
              <div className='name-and-logo-container'>
                {logoUrl && (
                  <div className='logo-container'>
                    <img src={logoUrl} />
                  </div>
                )}
                <div className='company-name-container'>
                  <div className='company-name'>
                    <span>{company.name}</span>
                  </div>
                  <div className='powered-by'>
                    <span>{'Powered by StartUP CRM'}</span>
                  </div>
                </div>
              </div>
              <QuickNote />

              <div className='search-and-avatar-container'>
                {!leftNavHidden && <Notifications />}

                {!leftNavHidden && !collapseSearch && (
                  <Box sx={{ mr: 1 }}>
                    <CustomerSearch
                      value={dummyAutocompleValue}
                      setValue={setDummyAutocompleteValue}
                      allowAdd
                      onSelect={onSelectOption}
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                    />
                  </Box>
                )}
                <IconButton
                  onClick={(e) => {
                    setAvatarMenuAnchorElement(e.currentTarget)
                    setIsAvatarMenuOpen(true)
                  }}
                >
                  {isInLionsClub && isAdmin
                    ? lionsClubBadgeWrapper(lionsClubTracker, avatar)
                    : avatar}
                </IconButton>
                <Menu
                  open={isAvatarMenuOpen}
                  anchorEl={avatarMenuAnchorElement}
                  onClose={() => setIsAvatarMenuOpen(false)}
                  onClick={() => setIsAvatarMenuOpen(false)}
                >
                  <Box sx={{ minWidth: '200px' }}></Box>
                  {collapseSearch && !leftNavHidden && (
                    <MenuItem onClick={(e) => e.stopPropagation()}>
                      <CustomerSearch
                        value={dummyAutocompleValue}
                        setValue={setDummyAutocompleteValue}
                        allowAdd
                        onSelect={(...args) => {
                          setIsAvatarMenuOpen(false)
                          onSelectOption(...args)
                        }}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                      />
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => navigate('/profile')}>
                    <ListItemIcon>
                      <ManageAccounts />
                    </ListItemIcon>
                    <ListItemText>My Profile</ListItemText>
                  </MenuItem>

                  {isAdmin && (
                    <MenuItem onClick={() => navigate('/billing')}>
                      <ListItemIcon>
                        <Paid />
                      </ListItemIcon>
                      <ListItemText>Billing</ListItemText>
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => apiClient.logout()}>
                    <ListItemIcon>
                      <Logout />
                    </ListItemIcon>
                    <ListItemText>Log out</ListItemText>
                  </MenuItem>
                </Menu>
              </div>
            </>
          )}
        </div>
        {isInDashboard && <DashboardTabs />}
      </Box>
    </>
  )
}

const lionsClubBadgeWrapper = (lionsClubTracker, children) => {
  return (
    <Badge color={'primary'} badgeContent={`#${lionsClubTracker}`}>
      {children}
    </Badge>
  )
}

export default Header
